import React from 'react';

import { ConsoleShell } from '../../../design-system/components/shells';
import { PageHeader, SectionTitle, Stat } from '../../../design-system/components/desktop';
import {
  ConsoleError,
  ConsoleStack,
  StatGrid,
  StatValue,
} from '../../../design-system/patterns/console';

/**
 * Admin dashboard, the view. Pure: every input is a prop, every output a
 * callback. `AdminDashboard.jsx` owns the query.
 *
 * ONE THING WAS DELETED RATHER THAN CONVERTED
 * -------------------------------------------
 * The old page carried a "System Overview" band with a Recent Activity list
 * ("New seller registration: Mike Store", "Payout processed: John Seller") and a
 * System Health panel reporting the server, the database and the payment gateway
 * as green. Every one of those nine values was a hardcoded string. There is no
 * activity feed endpoint and no health endpoint in `api/modules/admin.module.js`
 * or anywhere else, so the panel could only ever report "Online" and would keep
 * reporting it through an outage.
 *
 * A fabricated green health light on an admin console is worse than no health
 * light, so it is gone rather than restyled. It comes back when there is
 * something real behind it. Same call as the `mockBuyers` finding on
 * `/admin-buyers`, which is flagged in the UI instead because the rows there at
 * least carry a shape a real endpoint would fill.
 *
 * The orders table is `components/SellerDashboard/OrderTable/AllOrder`, which
 * belongs to batch B6 and is passed in rather than imported here, so this file
 * stays pure and B6 can swap it without touching this one.
 */
export function AdminDashboardView({ stats, loading = false, error, onRetry, ordersTable }) {
  const value = (key) => {
    const raw = stats?.[key];
    return raw === undefined || raw === null ? 0 : raw;
  };

  return (
    <ConsoleShell
      variant="admin"
      title="Dashboard"
      breadcrumbs={[{ label: 'Admin' }, { label: 'Dashboard' }]}
      showSearch={false}
    >
      <ConsoleStack>
        <PageHeader title="Dashboard" sub="Marketplace totals across every account." />

        {error ? (
          /* GLYPH: this replaces a row of stat tiles, not a table body. */
          <ConsoleError inline error={error} onRetry={onRetry} title="Could not load the totals" />
        ) : (
          <StatGrid>
            <Stat
              label="Total users"
              value={<StatValue loading={loading}>{value('total_users')}</StatValue>}
            />
            <Stat
              label="Total sellers"
              value={<StatValue loading={loading}>{value('total_sellers')}</StatValue>}
            />
            <Stat
              label="Total buyers"
              value={<StatValue loading={loading}>{value('total_buyers')}</StatValue>}
            />
            <Stat
              label="Total orders"
              value={<StatValue loading={loading}>{value('total_orders')}</StatValue>}
            />
            <Stat
              label="Amount requested"
              value={
                <StatValue loading={loading}>{stats?.formattedRequested ?? 'R0.00'}</StatValue>
              }
            />
          </StatGrid>
        )}

        <section>
          <SectionTitle>Recent orders</SectionTitle>
          {ordersTable}
        </section>
      </ConsoleStack>
    </ConsoleShell>
  );
}

export default AdminDashboardView;
